'use client';

import React, {
  createContext,
  use,
  useCallback,
  useMemo,
  ReactNode
} from 'react';
import { useProfileQuery } from '@/lib/services/profile/hooks';
import type { UserProfile } from '@/lib/services/profile/types';
import { getUserDisplayName } from '@/lib/utils/permissions';

interface UserProfileContextType {
  profile: UserProfile | null;
  displayName: string;
  initials: string;
  isLoading: boolean;
  error: Error | null;
  refetch: () => void;
}

const UserProfileContext = createContext<UserProfileContextType | undefined>(
  undefined
);

/**
 * Subscribes to the canonical `useProfileQuery` so the dashboard fires
 * a single `/profile` fetch shared with `FeatureFlagsProvider` and
 * every `useUserProfile` consumer.
 */
export function UserProfileProvider({ children }: { children: ReactNode }) {
  const { data, isLoading, error, refetch } = useProfileQuery();

  const profile: UserProfile | null = useMemo(() => {
    if (!data) return null;
    return 'user' in data ? data.user : (data as UserProfile);
  }, [data]);

  const displayName = useMemo(() => {
    if (!profile) return '';
    return getUserDisplayName(profile);
  }, [profile]);

  const initials = useMemo(() => {
    if (!profile) return '';
    const firstName = profile.firstName || '';
    const lastName = profile.lastName || '';
    if (firstName && lastName) {
      return `${firstName[0]}${lastName[0]}`.toUpperCase();
    }
    if (firstName) {
      return firstName.slice(0, 2).toUpperCase();
    }
    if (profile.email) {
      return profile.email.slice(0, 2).toUpperCase();
    }
    return 'U';
  }, [profile]);

  const refetchCallback = useCallback(() => {
    refetch();
  }, [refetch]);

  const value = useMemo<UserProfileContextType>(
    () => ({
      profile,
      displayName,
      initials,
      isLoading,
      error: error as Error | null,
      refetch: refetchCallback
    }),
    [profile, displayName, initials, isLoading, error, refetchCallback]
  );

  return <UserProfileContext value={value}>{children}</UserProfileContext>;
}

/**
 * Hook to use user profile from context
 * Prevents duplicate fetches across the app
 */
export function useUserProfileContext() {
  const context = use(UserProfileContext);
  if (context === undefined) {
    throw new Error(
      'useUserProfileContext must be used within a UserProfileProvider'
    );
  }
  return context;
}

/**
 * Optional hook for components that don't require the provider
 * Returns null values if provider is not available
 */
export function useOptionalUserProfile() {
  try {
    return useUserProfileContext();
  } catch {
    return {
      profile: null,
      displayName: '',
      initials: '',
      isLoading: false,
      error: null,
      refetch: () => {}
    };
  }
}
